/**
 * GeminiService.js
 * Handles AI interactions using the Google Generative AI SDK.
 * Implements Lessons Learned:
 * - Lesson 28: Exponential Backoff
 * - Lesson 30: Use 'gemini-2.5-flash-image' for image output
 * - Lesson 26: Mock Fallback for Dev Environment
 * - INC-20260121-2305: Handle inlineData output (Governance: Functional Source Integrity)
 */

/**
 * GeminiService.js
 * Handles AI interactions by calling the Secure Backend API.
 * 
 * Refactored (SDLC #7):
 * - Removed Client-Side API Key (Security Fix)
 * - Removed GoogleGenerativeAI SDK
 * - Now uses fetch() to call /api/ endpoints
 */

import { getAuth } from 'firebase/auth';

const getApiBaseUrl = () => {
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
};

export class GeminiService {

    /**
     * Get the current user's ID token
     */
    async _getToken() {
        try {
            const auth = getAuth();
            if (!auth.currentUser) return null;
            return await auth.currentUser.getIdToken();
        } catch(e) {
            return null;
        }
    }

    /**
     * Analyze Face (Verify Humanity)
     * POST /api/analyzeFace
     */
    async analyzeFace(base64Image) {
        try {
            const token = await this._getToken();
            const headers = {
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${getApiBaseUrl()}/api/analyzeFace`, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: JSON.stringify({ image: base64Image, themeId: 'emotions' })
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.message || 'Analysis Failed');
            }

            return await response.json();
        } catch (error) {
            console.error('[GeminiService] Analysis Error:', error);
            throw error;
        }
    }

    /**
     * Generate Preview (Initial Image)
     * POST /api/generatePreview
     */
    async generatePreview(emojiId, emojiChar, base64Image) {
        try {
            const token = await this._getToken();
            const headers = {
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const url = `${getApiBaseUrl()}/api/generatePreview`;
            const response = await fetch(url, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: JSON.stringify({
                    emojiId,
                    emojiChar,
                    emoji: emojiChar, // Legacy compatibility
                    image: base64Image,
                    themeId: 'emotions' // Explicitly set default theme
                })
            });

            if (!response.ok) {
                if (response.status === 402) {
                    const err = new Error('Insufficient Credits');
                    err.status = 402;
                    throw err;
                }
                throw new Error('Generation Failed');
            }

            const data = await response.json();
            return data.image; // Expecting Base64 Data URI
        } catch (error) {
            console.error('[GeminiService] Preview Error:', error);
            throw error;
        }
    }

    /**
     * Generate Collage (3 Variations)
     * POST /api/collages
     */
    async generateCollage(emojiId, emojiChar, base64Image) {
        try {
            const token = await this._getToken();
            const headers = {
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${getApiBaseUrl()}/api/collages`, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: JSON.stringify({
                    emojiId,
                    emojiChar,
                    matched_animal: emojiChar, // Backend expects snake_case mapping in route or 'animal'
                    image: base64Image,
                    theme_id: 'emotions'
                })
            });

            if (!response.ok) {
                if (response.status === 402) {
                    const err = new Error('Insufficient Credits');
                    err.status = 402;
                    throw err;
                }
                throw new Error('Collage Generation Failed');
            }

            const data = await response.json();
            // Backend returns { images: [null, var1, var2, var3], animals: [...] }
            return data;
        } catch (error) {
            console.error('[GeminiService] Collage Error:', error);
            throw error;
        }
    }

    /**
     * Mock Generation (Circuit Breaker Fallback)
     * Returns a placeholder SVG data URI when circuit breaker is open.
     */
    async mockGeneration(emoji) {
        const raw = typeof emoji === 'object' ? (emoji.label || emoji.emoji || '?') : (emoji || '?');
        // Sanitize to prevent XSS via tenant-supplied emoji labels
        const label = raw.replace(/[<>&"']/g, '');
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512">
            <rect width="512" height="512" fill="#f3f4f6" rx="40"/>
            <text x="256" y="220" text-anchor="middle" font-size="120">${label}</text>
            <text x="256" y="320" text-anchor="middle" font-size="24" fill="#9ca3af">AI Temporarily Unavailable</text>
            <text x="256" y="360" text-anchor="middle" font-size="16" fill="#d1d5db">Using fallback mode</text>
        </svg>`;
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }
}

export const geminiService = new GeminiService();
